// filepath: apps/web/src/editors/form-builder/FormSettingsPanel.tsx
//
// FR-UI-BLD-1 AC-2 — right-hand settings panel for the form builder.
// Renders the form-level `entity_type` + per-field settings for the
// selected field (label, placeholder, required, visible_when,
// computed_from, target_entity).
//
// INV-2: ALL fields render regardless of `visibleWhen` evaluation.
// The settings panel is an authoring surface, not a runtime renderer.

import React from "react";
import { useTranslation } from "react-i18next";
import type {
  EntityTypeSummary,
  FieldDefinition,
  FormDraft,
  ValidationErrorWire,
} from "./types";
import { FormulaInput } from "./FormulaInput";
import { InlineFieldError } from "./InlineFieldError";

interface FormSettingsPanelProps {
  draft: FormDraft;
  selectedFieldId?: string;
  onChangeSelectedField: (next: FieldDefinition) => void;
  onChangeEntityType: (next: string) => void;
  entityTypes: EntityTypeSummary[];
  errors: ValidationErrorWire[];
}

export function FormSettingsPanel({
  draft,
  selectedFieldId,
  onChangeSelectedField,
  onChangeEntityType,
  entityTypes,
  errors,
}: FormSettingsPanelProps): React.ReactElement {
  const { t } = useTranslation();
  const field = selectedFieldId ? draft.fields[selectedFieldId] : undefined;

  return (
    <aside
      className="form-settings-panel"
      aria-label={t("admin.builder.settings.aria")}
      data-testid="form-settings-panel"
    >
      <h2 className="form-settings-panel__title">
        {t("admin.builder.settings.title")}
      </h2>
      <div className="form-settings-panel__form-level">
        <label htmlFor="form-settings-entity-type">
          {t("admin.builder.settings.entity_type")}
        </label>
        <select
          id="form-settings-entity-type"
          value={draft.entity_type}
          onChange={(e) => onChangeEntityType(e.target.value)}
          data-testid="form-settings-entity-type"
        >
          <option value="">—</option>
          {entityTypes.map((et) => (
            <option key={et.name} value={et.name}>
              {et.name}
            </option>
          ))}
        </select>
        <InlineFieldError fieldId="entity_type" errors={errors} />
      </div>
      {field && selectedFieldId ? (
        <div className="form-settings-panel__field-level">
          <h3 className="form-settings-panel__field-title">
            {t("admin.builder.settings.field_heading", { id: selectedFieldId })}
          </h3>
          <label htmlFor="field-label">
            {t("admin.builder.settings.label")}
          </label>
          <input
            id="field-label"
            type="text"
            value={field.label ?? ""}
            onChange={(e) =>
              onChangeSelectedField({ ...field, label: e.target.value })
            }
            data-testid="field-label-input"
          />
          <label htmlFor="field-placeholder">
            {t("admin.builder.settings.placeholder")}
          </label>
          <input
            id="field-placeholder"
            type="text"
            value={field.placeholder ?? ""}
            onChange={(e) =>
              onChangeSelectedField({
                ...field,
                placeholder: e.target.value,
              })
            }
            data-testid="field-placeholder-input"
          />
          <label className="form-settings-panel__toggle">
            <input
              type="checkbox"
              checked={Boolean(field.required)}
              onChange={(e) =>
                onChangeSelectedField({ ...field, required: e.target.checked })
              }
              data-testid="field-required-checkbox"
            />
            {t("admin.builder.settings.required")}
          </label>
          <label htmlFor="field-visible-when">
            {t("admin.builder.settings.visible_when")}
          </label>
          <FormulaInput
            fieldId={selectedFieldId}
            context="visibleWhen"
            value={field.visible_when ?? ""}
            onChange={(next) =>
              onChangeSelectedField({ ...field, visible_when: next })
            }
            ariaLabel={t("admin.builder.settings.visible_when")}
          />
          <label htmlFor="field-computed-from">
            {t("admin.builder.settings.computed_from")}
          </label>
          <FormulaInput
            fieldId={selectedFieldId}
            context="computedFrom"
            value={field.computed_from ?? ""}
            onChange={(next) =>
              onChangeSelectedField({ ...field, computed_from: next })
            }
            ariaLabel={t("admin.builder.settings.computed_from")}
          />
          {field.field_type === "reference" && (
            <>
              <label htmlFor="field-target-entity">
                {t("admin.builder.settings.target_entity")}
              </label>
              <select
                id="field-target-entity"
                value={field.target_entity ?? ""}
                onChange={(e) =>
                  onChangeSelectedField({
                    ...field,
                    target_entity: e.target.value,
                  })
                }
                data-testid="field-target-entity-select"
              >
                <option value="">—</option>
                {entityTypes
                  .filter((et) => et.name !== draft.entity_type)
                  .map((et) => (
                    <option key={et.name} value={et.name}>
                      {et.name}
                    </option>
                  ))}
              </select>
            </>
          )}
          <InlineFieldError fieldId={selectedFieldId} errors={errors} />
        </div>
      ) : (
        <p className="form-settings-panel__no-selection">
          {t("admin.builder.settings.no_selection")}
        </p>
      )}
    </aside>
  );
}
